import { useApolloClient, useMutation } from '@apollo/client'
import { type ViewFieldsFragment, WriteEltChangesChunkDocument } from '@salescore/client-api'
import { Posthog, POSTHOG_EVENTS, Tracker } from '@salescore/client-base'
import { getOrganizationIdFromPath, useClearViewQueryCache } from '@salescore/client-common'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  CORE_CONSTANT,
  type EltChangeResult,
  type EltChangesChunk,
  type EltChangeToDelete,
  flatNodes,
  type ModelSearcher,
  type ViewConfigTreeNode,
  type ViewQueryList,
  type ViewQueryRecordNode,
  type ViewQueryTableNode,
} from '@salescore/core'
import { pivotedGoalModelWithoutProperties } from '@salescore/features'
import { message } from 'antd'
import { Array, pipe, Record } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type Dispatch, type SetStateAction, useMemo } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import { partitionPickedRecordIds } from '../../../domain/partitionPickedRecordIds'
import { getChangesChunks } from '../../../domain/recordsHistory/recordsHistorySelectors'
import { recordsAction } from '../../../state/useViewRecordsState/recordsAction'
import { useRecordsImpl, useSetRecordsImpl } from '../../records'
import { changesAtom, type ChangesWithTransactionUpsertStatus, useChangesMutation } from '../../recordsHistory'
import { viewsContextAtom } from '../../view/atoms'
import { useModelSearcher } from '../../view/selectors/modelSearcherSelector'
import { configAtom } from '../../viewConfig'
import { errorsAtom, pickedIdsAtom } from '../atoms'
import { useIsSavingState } from '../hooks'
import { useRefetchAggregationQueryResult } from './useRefetchAggregationQueryResult'
import { useRefetchMutation } from './useRefetchMutation'

const NOT_FOUND_PLACEHOLDER = '_NOT_FOUND_'

const getChangedProperties = (
  changesChunks: EltChangesChunk[],
): Array<{
  modelName: string
  propertyName?: string
  type: string
}> =>
  pipe(
    changesChunks,
    Array.flatMap((x) =>
      pipe(
        x.changes,
        Array.flatMap((change) => {
          switch (change.type) {
            case 'create': {
              return pipe(
                Record.keys(change.data),
                Array.map((propertyName) => ({
                  modelName: change.modelName,
                  propertyName,
                  type: 'create',
                })),
              )
            }
            case 'update': {
              return pipe(
                Record.keys(change.data),
                Array.map((propertyName) => ({
                  modelName: change.modelName,
                  propertyName,
                  type: 'update',
                })),
              )
            }
            case 'delete': {
              return [{ modelName: change.modelName, type: 'delete' }]
            }
            // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
            default: {
              throw new Error(change satisfies never)
            }
          }
        }),
      ),
    ),
  )

export const useRecordsAndChangesMutation = (
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
  query: ViewQueryList | undefined,
) => {
  const refetchMutation = useRefetchMutation(view, query)
  const refetchAggregationQueryResult = useRefetchAggregationQueryResult(compiledConfig, view)
  const context = useRecoilValue(viewsContextAtom)
  const records = useRecordsImpl()
  const changesMutation = useChangesMutation(view)
  const modelSearcher = useModelSearcher()
  const client = useApolloClient()
  const pickedIds = useRecoilValue(pickedIdsAtom)
  const changes = useRecoilValue(changesAtom)
  const config = useRecoilValue(configAtom)
  const changesChunks = useMemo(() => getChangesChunks(changes, config), [changes, config])
  const { clearTargetViewQueryCaches } = useClearViewQueryCache()

  const setPickedIds = useSetRecoilState(pickedIdsAtom)
  const setErrors = useSetRecoilState(errorsAtom)
  const setRecords = useSetRecordsImpl()

  const savingFlag = useIsSavingState()

  const [writeEltChangesChunkMutation] = useMutation(WriteEltChangesChunkDocument)

  async function upsertRecords(changesChunks: EltChangesChunk[]): Promise<ChangesWithTransactionUpsertStatus> {
    if (savingFlag.isTrue) {
      return {
        status: 'notUpserted',
      }
    }
    savingFlag.setTrue()

    try {
      Tracker.shared().track({ category: 'saveRecords', value: changesChunks.length.toString() })
      const result = await writeEltChangesChunkMutation({
        variables: {
          organizationId: getOrganizationIdFromPath(),
          changesChunks,
          viewId: view.id,
        },
      })

      // シートのコンポーネントを目標入力UIでも流用しているため、目標入力時もここのロジックを通るが
      // 別で記録したいので分ける
      const isGoalView = changesChunks.some((x) => x.modelName === pivotedGoalModelWithoutProperties.name)
      if (isGoalView) {
        const goalConfigId = globalThis.location.pathname.split('/').at(-1)
        Posthog.track(POSTHOG_EVENTS.save_goals, {
          goalConfigId,
          changesCount: changesChunks.flatMap((x) => x.changes).length,
        })
      } else {
        Posthog.track(POSTHOG_EVENTS.save_records, {
          changesCount: changesChunks.flatMap((x) => x.changes).length,
          viewId: view.id,
          viewName: view.name,
          changedProperties: getChangedProperties(changesChunks),
          prevViewId: context.prevView?.id,
          prevViewType: context.prevView?.type,
        })
      }
      // エラーが発生しなかったときだけrefetchする
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: 必要な行だけをrefetchするような形にしたい
      const results = result.data?.writeEltChangesChunk.flatMap((x) => x.results) ?? []
      const errors = results.filter((x) => x.status !== 'success')
      if (errors.length === 0) {
        const cacheClearViewIds = [
          context.prevView?.id,
          view.id.replace(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX, ''),
        ].filter(isNotNullish)
        clearTargetViewQueryCaches({ viewIds: cacheClearViewIds })
        await refetchMutation.refetch({ kind: 'save' })
      } else {
        Posthog.track(POSTHOG_EVENTS.failed_save_sheet_records, {
          viewId: view.id,
          viewName: view.name,
          errorsCount: errors.length,
          errorTexts: pipe(
            errors,
            Array.map((x) => x.error),
            Array.dedupe,
          ).join(','),
          prevViewId: context.prevView?.id,
          prevViewType: context.prevView?.type,
        })
      }
      await refetchAggregationQueryResult.refetch()
      return {
        status: 'upserted',
        results: result.data?.writeEltChangesChunk ?? [],
      }
    } catch (error) {
      return {
        status: 'upsertError',
        error,
      }
    } finally {
      savingFlag.setFalse()
    }
  }

  return {
    ...recordsAction({
      records,
      query,
      client,
      setRecords,
      addChange(x) {
        changesMutation.addChange(x)
      },
    }),
    async destroyPickedRecords(isDeleteAllChildren?: boolean) {
      // query === undefined は kpi/kpiPivot view 経由で本 hook が mount された場合のみ。
      // kpi/kpiPivot の column 型では本分岐 (ピック行削除) に到達しないため silent return で安全。
      if (view.config.type !== 'sheet' || view.config.tree === undefined || query === undefined) {
        return
      }
      // 新規行と既存行で分ける
      const { newRecordIds: newIds, existingRecordIds: existIds } = partitionPickedRecordIds(pickedIds)
      // const rowIndex = records.findIndex((record)=>record.id === )

      if (newIds.length > 0) {
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: この処理だと、ルートからしか取り除けない。現状pickできるidはルートのみなので問題ないが、今後必要であれば拡張
        const newRecords = records.filter((record) => !newIds.includes(record.id))
        setRecords(newRecords)

        changesMutation.removeChanges(newIds)
        setPickedIds((oldPickedIds) => oldPickedIds.filter((id) => !newIds.includes(id)))
      }

      if (existIds.length > 0) {
        const streamName = query.tree.write?.streamName
        if (streamName === undefined || existIds.length === 0) {
          void message.error(t(`更新不可能なオブジェクトです`))
          return
        }

        const changesChunks: EltChangesChunk[] =
          isDeleteAllChildren === true
            ? generateChunksForDeleteAllChildren({
                records: records.filter(({ id }) => existIds.includes(id)),
                tree: view.config.tree,
                modelSearcher,
              })
            : [
                {
                  modelName: streamName,
                  changes: existIds.map((id): EltChangeToDelete => ({
                    type: 'delete',
                    modelName: streamName,
                    id,
                    // eslint-disable-next-line sonarjs/todo-tag
                    before: {}, // TODO
                  })),
                },
              ]

        const result = await upsertRecords(changesChunks)
        Posthog.track(POSTHOG_EVENTS.save_records, {
          changesCount: changesChunks.flatMap((x) => x.changes).length,
          viewId: view.id,
          viewType: view.type,
          viewName: view.name,
          changedProperties: getChangedProperties(changesChunks),
          prevViewId: context.prevView?.id,
          prevViewType: context.prevView?.type,
        })
        if (result.status === 'upserted') {
          const errorResults = result.results
            .flatMap((x) => x.results)
            .map((x) => (x.status === 'success' ? undefined : x))
            .filter(isNotNullish)
          if (errorResults.length > 0) {
            const errorTexts = errorResults.map((x) => x.error).filter(isNotNullish)
            void message.error(errorTexts.join(','))
            setErrors(errorTexts)
            const errorIds = errorResults.map((x) => x.change.id).filter(isNotNullish)
            setPickedIds(errorIds)
            void message.error(t(`{{length}}件のレコードでエラーが発生しました。`, { length: errorIds.length }))
            Posthog.track(POSTHOG_EVENTS.failed_save_sheet_records, {
              viewId: view.id,
              viewType: view.type,
              viewName: view.name,
              errorsCount: errorIds.length,
              errorTexts: errorTexts.join(','),
              prevViewId: context.prevView?.id,
              prevViewType: context.prevView?.type,
            })
          } else {
            setPickedIds([])
            void message.success(t(`削除しました`))
          }
        } else {
          void message.error(t(`エラーが発生しました`))
        }
      }
    },
    async onSave(setRecordSaveErrors: Dispatch<SetStateAction<EltChangeResult[]>>) {
      if (savingFlag.isTrue) {
        return
      }
      return await changesMutation.resetChangesWithTransaction(
        async () => await upsertRecords(changesChunks),
        setRecordSaveErrors,
      )
    },
  }
}

function flatChildrenTableNodes(records: ViewQueryRecordNode[]): ViewQueryTableNode[] {
  // 与えられたrecordsに属する全てのViewQueryTableNodeを返す
  return records.flatMap((record) =>
    record.children.flatMap((child) => [child, ...flatChildrenTableNodes(child.children)]),
  )
}

function generateChunksForDeleteAllChildren({
  records,
  tree,
  modelSearcher,
}: {
  records: ViewQueryRecordNode[]
  tree: ViewConfigTreeNode
  modelSearcher: ModelSearcher
}): EltChangesChunk[] {
  const rootModelName = tree.modelName
  const flattenTree = flatNodes(tree)
  const treeModels = flattenTree.map((x) => ({ nodeName: x.name, modelName: x.modelName }))
  const flattenChildrenTableNodes = [
    {
      nodeName: rootModelName,
      children: records,
    },
    ...flatChildrenTableNodes(records),
  ]
  const groupedTableNodes = Array.groupBy(flattenChildrenTableNodes, (node) => {
    const model = treeModels.find((x) => x.nodeName === node.nodeName)
    return model?.modelName ?? NOT_FOUND_PLACEHOLDER // 見つからなかった場合は後で除外するために定数値を入れる
  })

  const chunks: EltChangesChunk[] = Object.entries(groupedTableNodes)
    .map(([modelName, nodes]) => {
      if (modelName === NOT_FOUND_PLACEHOLDER) {
        // eslint-disable-next-line array-callback-return
        return
      }

      // 削除不可なモデルはスキップ
      const model = modelSearcher.searchModel(modelName)
      if (!(model?.deletable ?? true)) {
        // eslint-disable-next-line array-callback-return
        return
      }

      const deleteIds = pipe(
        nodes,
        Array.flatMap((node) => node.children.map((x) => x.id)),
        Array.filter(isNotNullish),
        Array.dedupe,
      )
      if (deleteIds.length === 0) {
        // eslint-disable-next-line array-callback-return
        return
      }

      return {
        modelName,
        changes: deleteIds.map((id): EltChangeToDelete => ({
          type: 'delete',
          modelName,
          id,
          before: {},
        })),
      }
    })
    .filter(isNotNullish)

  return chunks
}
